<template>
    <div>
        <div v-if="!readyToLoad" class="text-center py-12 my-12" style="width: 100%">
            <v-progress-circular
                indeterminate 
                color="primary darken-1" 
                class="my-12 py-12" 
                size="100" 
                width="6">
            </v-progress-circular>
        </div>
    
        <div v-else-if="readyToLoad && tasks.length" class="my-8 pb-4">            
            <h4 class="questionnaire_title mb-3">Task Change History</h4>
            <v-data-table 
                :headers="headers"
                :items="tasks"
                :options.sync="table_options"
                fixed-header
                hide-default-footer>

                <template v-slot:item="{ item }">
                    <tr :key="item.data_mod_id"
                        class="d-table-row">

                        <td class="text-start">{{ datapointTextFormatting(item.data_point) }}</td>
                        <td class="text-start">{{ datapointValueFormatting(item.data_point, item.previous_value) }}</td>
                        <td class="text-start">{{ datapointValueFormatting(item.data_point, item.new_value) }}</td>
                        <td class="text-start">{{ item.user_id ? ownerItems[item.user_id] : '' }}</td>
                        <td class="text-end">{{ formatThisDate(item.modified_date, modified_date_options) }}</td>
                    </tr>
                </template>
            </v-data-table>
        </div>
    </div>
</template>

<script>
    import axios from 'axios';
    import Cookie from 'js-cookie'
    import appUrls from '../helpers/environment';
    import { dataTimeDisplayFormatter } from './utilities'
    import { 
        complexityItems,
        durationItems,
        ownerItems,
        priorityItems,
        taskStatusItems, 
        responsiblePartyItems,
    } from '../helpers/constants'

    export default {
        name: 'ProjectReportTaskHistory',

        props: {
            task_id: String
        },
    
        data() {
            return {
                tasks: [],
                readyToLoad: false,
                headers: [
                    { text: 'Data Point',       value: 'data_point',        align: 'start' },
                    { text: 'Previous Value',   value: 'previous_value',    align: 'start' },
                    { text: 'New Value',        value: 'new_value',         align: 'start' },
                    { text: 'Modified By',      value: 'user_id',           align: 'start' },
                    { text: 'Date Modified',    value: 'modified_date',     align: 'end' }
                ],
                ownerItems: ownerItems,
                complexityItems: complexityItems,
                durationItems: durationItems,
                priorityItems: priorityItems,
                taskStatusItems: taskStatusItems,
                responsiblePartyItems: responsiblePartyItems,
                table_options: {
                    sortBy: ['modified_date'],
                    sortDesc: [true],
                    itemsPerPage: -1,
                    multiSort: true
                },
                modified_date_options: {
                    month: '2-digit', 
                    day: '2-digit', 
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                    timeZoneName: 'short'
                },
            }
        },

        watch: {
            task_id(newVal) {
                if (newVal) {
                    this.tasks = [];
                    this.readyToLoad = false;
                    this.getTaskHistory();
                }
            }
        },

        mounted() {
            this.getTaskHistory();
        },

        methods: {
            getTaskHistory: async function () {
                try {
                    const task_history = await axios.get(`${appUrls.api}/api/admin/tasks/${this.task_id}/task_history`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                    });
                    this.tasks = task_history.data.task_history;
                } catch (error) {
                    console.error('Error fetching task history:', error);
                } finally {
                    this.readyToLoad = true;
                }
            },

            formatThisDate: function ( date, options, iso_format=false ) {
                if (!date) return '';

                const defaultOptions = {
                    month: '2-digit', 
                    day: '2-digit', 
                    year: 'numeric'
                }
                return dataTimeDisplayFormatter(date, options ? options : defaultOptions, iso_format )
            },
            
            datapointTextFormatting ( datapoint ) {
                let formatted_datapoint = datapoint;

                switch ( datapoint ) {
                    case 'date':                formatted_datapoint = 'due date';           break;
                    case 'status':              formatted_datapoint = 'status';             break;
                    case 'complexity':          formatted_datapoint = 'complexity';         break;
                    case 'duration':            formatted_datapoint = 'duration';           break;
                    case 'priority':            formatted_datapoint = 'priority';           break; 
                    case 'responsible_party':   formatted_datapoint = 'responsibility';     break;
                    case 'internal':            formatted_datapoint = 'internal';           break;
                    case 'owner_id':            formatted_datapoint = 'owner';              break;
                    case 'label':               formatted_datapoint = 'label';              break;
                    default:                    formatted_datapoint = datapoint;            break;
                }

                return formatted_datapoint;
            },

            datapointValueFormatting ( datapoint, value ) {
                let formatted_value = value;

                switch ( datapoint ) {
                    case 'date':
                        formatted_value = ['', 'NULL', undefined].includes(value) ? '---' : this.formatThisDate(value);
                        break;
                    case 'status':
                        formatted_value = ['', 'NULL', undefined].includes(value) ? '---' : this.taskStatusItems[value] || value;
                        break;
                    case 'complexity':
                        formatted_value = ['', 'NULL', undefined].includes(value) ? '---' : this.complexityItems[value] || value;
                        break;
                    case 'duration':
                        formatted_value = ['', 'NULL', undefined].includes(value) ? '---' : this.durationItems[value] || value;
                        break;
                    case 'priority':
                        formatted_value = ['', 'NULL', undefined].includes(value) ? '---' : this.priorityItems[value] || value;
                        break; 
                    case 'responsible_party':
                        formatted_value = ['', 'NULL', undefined].includes(value) ? '---' : this.responsiblePartyItems[value] || value;
                        break;
                    case 'internal':
                        formatted_value = ['', 'NULL', undefined].includes(value) ? '---' : value === true ? 'Yes' : 'No';
                        break;
                    case 'owner_id':
                        formatted_value = ['', 'NULL', undefined].includes(value) ? '---' : this.ownerItems[value] || value;
                        break;
                    case 'label':
                        formatted_value = ['', 'NULL', undefined].includes(value) ? '---' : value;
                        break;
                    default:
                        formatted_value = value;
                }

                return formatted_value;
            },
        }
    }
</script>

<style scoped>
 >>> .v-data-table .v-data-table__wrapper > table > thead > tr > th {
        padding: 0 .8em !important;
        font-size: x-small;
        color: #333 !important;
        margin: 0 !important;
        background-color: whitesmoke !important;
        border: none !important;
    }
 >>> .v-data-table .v-data-table__wrapper > table > tbody > tr > td {
        padding: 0 .8em !important;
        margin: 0 !important;
        font-size: small;
    }
 >>> .v-data-table .v-data-table__wrapper > table > tbody > tr:nth-of-type(even) {
        background-color: whitesmoke !important;
    }
 >>> .v-data-table .v-data-table__wrapper > table > tbody > tr:nth-of-type(odd) {
        background-color: rgb(248, 252, 252) !important;
    }
    /* .task_history_data {
        padding: 0 .2rem !important;
        margin: 0 !important;
        font-family: 'AvenirBook' !important;
    } */
</style>


// WEBPACK FOOTER //
// src/components/ProjectReportTaskHistory.vue